import { ManualState, ManualAction } from "../types";
import { extractChapterTitle } from "../utils/serializers";

export const initialState: ManualState = {
  chapters: {},
  subChapters: {},
  chapterOrder: [],
  selectedChapterId: null,
  selectedSubChapterId: null,
  hasUnsavedChanges: false,
  lastChangeTimestamp: 0,
  isLoading: false,
};

export const manualReducer = (
  state: ManualState,
  action: ManualAction,
): ManualState => {
  switch (action.type) {
    case "SET_INITIAL_DATA":
      return {
        ...state,
        ...action.data,
        isLoading: false,
      };

    case "UPDATE_CHAPTER_CONTENT":
      // Extract title from content to keep it in sync
      const newTitle = extractChapterTitle(action.content);
      return {
        ...state,
        chapters: {
          ...state.chapters,
          [action.chapterId]: {
            ...state.chapters[action.chapterId],
            content: action.content,
            title: newTitle, // Auto-update title from content
            updatedAt: Date.now(),
          },
        },
        hasUnsavedChanges: true,
        lastChangeTimestamp: Date.now(), // Update timestamp to restart countdown
      };

    case "ADD_CHAPTER":
      return {
        ...state,
        chapters: {
          ...state.chapters,
          [action.chapter.id]: action.chapter,
        },
        chapterOrder: [...state.chapterOrder, action.chapter.id],
        hasUnsavedChanges: true,
        lastChangeTimestamp: Date.now(), // Update timestamp to restart countdown
      };

    case "DELETE_CHAPTER":
      const { [action.chapterId]: deletedChapter, ...remainingChapters } =
        state.chapters;
      const newOrder = state.chapterOrder.filter(
        (id) => id !== action.chapterId,
      );

      return {
        ...state,
        chapters: remainingChapters,
        chapterOrder: newOrder,
        selectedChapterId:
          state.selectedChapterId === action.chapterId
            ? null
            : state.selectedChapterId,
        hasUnsavedChanges: true,
        lastChangeTimestamp: Date.now(), // Update timestamp to restart countdown
      };

    case "REORDER_CHAPTERS":
      // Update order property for each chapter
      const reorderedChapters = { ...state.chapters };
      action.newOrder.forEach((chapterId, index) => {
        if (reorderedChapters[chapterId]) {
          reorderedChapters[chapterId] = {
            ...reorderedChapters[chapterId],
            order: index,
          };
        }
      });

      return {
        ...state,
        chapters: reorderedChapters,
        chapterOrder: action.newOrder,
        hasUnsavedChanges: true,
        lastChangeTimestamp: Date.now(), // Update timestamp to restart countdown
      };

    case "SELECT_CHAPTER":
      return {
        ...state,
        selectedChapterId: action.chapterId,
        selectedSubChapterId: null, // Clear sub-chapter selection
      };

    case "SELECT_SUBCHAPTER":
      return {
        ...state,
        selectedSubChapterId: action.subChapterId,
      };

    case "MARK_SAVED":
      return {
        ...state,
        hasUnsavedChanges: false,
      };

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.isLoading,
      };

    default:
      return state;
  }
};
